import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import WidgetPane from '@/core/deep-dive/components/widget-pane';
import { SidePanelTab } from '@/core/deep-dive/components/side-panel';
import { getDeepDiveApp, isDeepDiveAppVisible } from '@/core/deep-dive/services/app-registry';
import { useSidePanelTabs } from '@/core/deep-dive/hooks/use-side-panel-tabs';
import { Permissions } from '@/core/permissions/export';
import { dashboardWidgetRegistry, useActiveMobileWidget } from '@/core/widgets/export';
import MobileSidePanelBar, { MobileSidePanelBarItem } from './mobile-side-panel-bar';
import { MobileFullscreenPanel } from '@/shared/components/export';

const BOOKMARKS_APP_ID = 'bookmarks';

export default function MobileSidePanel({ tabs }: { tabs: SidePanelTab[] }) {
  const {
    sidePanelUi,
    permissions,
    isOpen,
    isRailBrowseActive,
    isAppActive,
    toggleRail,
    toggleApp,
  } = useSidePanelTabs();
  const { activeMobileWidgetId } = useActiveMobileWidget();

  const hasDeepDiveAccess = permissions.includes(Permissions.DEEP_DIVE_ACCESS);

  const currentWidgetDeepDiveId = activeMobileWidgetId
    ? dashboardWidgetRegistry.getById(activeMobileWidgetId)?.deepDiveId
    : undefined;

  // Exactly 3 fixed slots: info, bookmarks, and the current widget's deep
  // dive (if it has one) — replaces the generic pinned-apps list for mobile.
  const items: MobileSidePanelBarItem[] = [
    ...(hasDeepDiveAccess
      ? [
          {
            id: 'deep-dive',
            icon: <FontAwesomeIcon icon={faCircleInfo} size="lg" />,
            label: 'Widgets',
            active: isRailBrowseActive,
            onClick: toggleRail,
          },
        ]
      : []),
    ...[BOOKMARKS_APP_ID, currentWidgetDeepDiveId].flatMap(id => {
      if (!id) return [];
      const app = getDeepDiveApp(id);
      if (!app || !isDeepDiveAppVisible(app, permissions)) return [];
      return [
        {
          id,
          icon: app.icon,
          label: app.label ?? app.defaultTitle,
          active: isAppActive(id),
          onClick: () => toggleApp(id),
        },
      ];
    }),
  ];

  if (items.length === 0) return null;

  const activeTab = tabs.find(tab => tab.id === sidePanelUi.openPanelId);

  const closeActivePanel = () => {
    if (sidePanelUi.openPanelId) sidePanelUi.closePanel(sidePanelUi.openPanelId);
  };

  return (
    <>
      <MobileSidePanelBar items={items} />
      {isOpen && activeTab && (
        <MobileFullscreenPanel header={activeTab.widget.headerPanel} onClose={closeActivePanel}>
          <WidgetPane widget={activeTab.widget} panelId={activeTab.id} />
        </MobileFullscreenPanel>
      )}
    </>
  );
}
